import React, { useState, useMemo } from 'react';
import { 
  Search, 
  Filter, 
  X, 
  ChevronDown, 
  ChevronUp,
  Users,
  UserCheck,
  UserX,
  Clock,
  Download,
  Upload,
  Trash2,
  Shield,
  ShieldCheck,
  ShieldX,
  MoreHorizontal,
  Settings,
  Eye,
  EyeOff,
  RefreshCw,
  Check,
  AlertTriangle
} from 'lucide-react';
import '../../css/components/Analytics/DropdownStyles.css';

// Enhanced filter configuration
const FILTER_OPTIONS = [
  { value: 'all', label: 'All Users', icon: Users, count: 0 },
  { value: 'active', label: 'Active', icon: UserCheck, count: 0 },
  { value: 'pending', label: 'Pending', icon: Clock, count: 0 },
  { value: 'blocked', label: 'Blocked', icon: UserX, count: 0 },
  { value: 'inactive', label: 'Inactive', icon: UserX, count: 0 },
  { value: 'admin', label: 'Admins', icon: ShieldCheck, count: 0 }
];

const BULK_ACTIONS = [
  { value: 'approve', label: 'Approve Selected', icon: ShieldCheck, color: '#10b981' },
  { value: 'block', label: 'Block Selected', icon: ShieldX, color: '#ef4444' },
  { value: 'activate', label: 'Activate Selected', icon: UserCheck, color: '#3b82f6' },
  { value: 'stop', label: 'Stop Selected', icon: UserX, color: '#6b7280' },
  { value: 'delete', label: 'Remove Selected', icon: Trash2, color: '#dc2626' }
];

// Enhanced search input component
const SearchInput = ({ 
  value, 
  onChange, 
  placeholder, 
  theme, 
  currentThemeName,
  onClear 
}) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <div style={{
      position: 'relative',
      flex: 1,
      maxWidth: 400
    }}>
      <div style={{
        position: 'absolute',
        left: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        color: currentThemeName === 'dark' ? '#6b7280' : theme.textTertiary,
        zIndex: 1
      }}>
        <Search size={18} />
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          width: '100%',
          padding: '12px 40px 12px 40px',
          borderRadius: 12,
          border: isFocused
            ? (currentThemeName === 'dark' ? '2px solid #3b82f6' : '2px solid #3b82f6')
            : (currentThemeName === 'dark' ? '1px solid rgba(59, 130, 246, 0.2)' : '1px solid rgba(59, 130, 246, 0.1)'),
          background: currentThemeName === 'dark' 
            ? 'rgba(30, 41, 59, 0.8)' 
            : 'rgba(255, 255, 255, 0.9)',
          color: currentThemeName === 'dark' ? 'white' : theme.text,
          fontSize: 14,
          transition: 'all 0.2s ease',
          outline: 'none',
          backdropFilter: 'blur(10px)'
        }}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        aria-label="Search users"
      />
      {value && (
        <button
          onClick={onClear}
          style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            color: currentThemeName === 'dark' ? '#6b7280' : theme.textTertiary,
            cursor: 'pointer',
            padding: 4,
            borderRadius: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = currentThemeName === 'dark' ? '#9ca3af' : theme.textSecondary;
            e.currentTarget.style.backgroundColor = currentThemeName === 'dark' 
              ? 'rgba(59, 130, 246, 0.1)' 
              : 'rgba(59, 130, 246, 0.05)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = currentThemeName === 'dark' ? '#6b7280' : theme.textTertiary;
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          aria-label="Clear search"
        >
          <X size={16} />
        </button>
      )}
    </div>
  );
};

// Enhanced filter dropdown component
const FilterDropdown = ({ 
  value, 
  onChange, 
  options, 
  theme, 
  currentThemeName,
  isOpen,
  onToggle 
}) => {
  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '12px 16px',
          borderRadius: 12,
          border: currentThemeName === 'dark' 
            ? '1px solid rgba(59, 130, 246, 0.2)' 
            : '1px solid rgba(59, 130, 246, 0.1)',
          background: currentThemeName === 'dark' 
            ? 'rgba(30, 41, 59, 0.8)' 
            : 'rgba(255, 255, 255, 0.9)',
          color: currentThemeName === 'dark' ? 'white' : theme.text,
          fontSize: 14,
          fontWeight: 500,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          backdropFilter: 'blur(10px)',
          minWidth: 160
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = currentThemeName === 'dark' 
            ? 'rgba(59, 130, 246, 0.3)' 
            : 'rgba(59, 130, 246, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = currentThemeName === 'dark' 
            ? 'rgba(59, 130, 246, 0.2)' 
            : 'rgba(59, 130, 246, 0.1)';
        }}
        aria-label="Filter users by status"
        aria-expanded={isOpen}
      >
        {selectedOption && (
          <>
            <selectedOption.icon size={16} />
            <span>{selectedOption.label}</span>
            {selectedOption.count > 0 && (
              <span style={{
                background: currentThemeName === 'dark' 
                  ? 'rgba(59, 130, 246, 0.2)' 
                  : 'rgba(59, 130, 246, 0.1)',
                color: currentThemeName === 'dark' ? '#3b82f6' : '#3b82f6',
                padding: '2px 8px',
                borderRadius: 12,
                fontSize: 12,
                fontWeight: 600
              }}>
                {selectedOption.count}
              </span>
            )}
          </>
        )}
        {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: 4,
          background: currentThemeName === 'dark' 
            ? 'rgba(30, 41, 59, 0.95)' 
            : 'rgba(255, 255, 255, 0.95)',
          border: currentThemeName === 'dark'
            ? '1px solid rgba(59, 130, 246, 0.2)'
            : '1px solid rgba(59, 130, 246, 0.1)',
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          zIndex: 1000,
          backdropFilter: 'blur(10px)',
          overflow: 'hidden'
        }}>
          {options.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  onToggle();
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'transparent',
                  color: currentThemeName === 'dark' ? '#e5e7eb' : theme.text,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  fontSize: 14,
                  transition: 'background-color 0.2s ease',
                  borderBottom: currentThemeName === 'dark' 
                    ? '1px solid rgba(59, 130, 246, 0.1)' 
                    : '1px solid rgba(59, 130, 246, 0.05)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = currentThemeName === 'dark' 
                    ? 'rgba(59, 130, 246, 0.1)' 
                    : 'rgba(59, 130, 246, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                aria-label={`Filter by ${option.label}`}
              >
                <Icon size={16} />
                <span style={{ flex: 1, textAlign: 'left' }}>{option.label}</span>
                {option.count > 0 && (
                  <span style={{
                    background: currentThemeName === 'dark' 
                      ? 'rgba(59, 130, 246, 0.2)' 
                      : 'rgba(59, 130, 246, 0.1)',
                    color: currentThemeName === 'dark' ? '#3b82f6' : '#3b82f6',
                    padding: '2px 8px',
                    borderRadius: 12,
                    fontSize: 12,
                    fontWeight: 600
                  }}>
                    {option.count}
                  </span>
                )}
                {value === option.value && (
                  <Check size={16} style={{ color: '#3b82f6' }} />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Enhanced bulk actions component
const BulkActions = ({ 
  selectedUsers, 
  bulkAction, 
  setBulkAction, 
  onBulkAction, 
  theme, 
  currentThemeName 
}) => {
  const [isOpen, setIsOpen] = useState(false);

  if (selectedUsers.length === 0) return null;

  const selectedAction = BULK_ACTIONS.find(action => action.value === bulkAction);

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '12px 16px',
          borderRadius: 12,
          border: currentThemeName === 'dark' 
            ? '1px solid rgba(59, 130, 246, 0.2)' 
            : '1px solid rgba(59, 130, 246, 0.1)',
          background: currentThemeName === 'dark' 
            ? 'rgba(30, 41, 59, 0.8)' 
            : 'rgba(255, 255, 255, 0.9)',
          color: currentThemeName === 'dark' ? 'white' : theme.text,
          fontSize: 14,
          fontWeight: 500,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          backdropFilter: 'blur(10px)',
          minWidth: 180
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = currentThemeName === 'dark' 
            ? 'rgba(59, 130, 246, 0.3)' 
            : 'rgba(59, 130, 246, 0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = currentThemeName === 'dark' 
            ? 'rgba(59, 130, 246, 0.2)' 
            : 'rgba(59, 130, 246, 0.1)';
        }}
        aria-label="Select bulk action"
        aria-expanded={isOpen}
      >
        {selectedAction ? (
          <>
            <selectedAction.icon size={16} style={{ color: selectedAction.color }} />
            <span>{selectedAction.label}</span>
          </>
        ) : (
          <>
            <MoreHorizontal size={16} />
            <span>Bulk Actions</span>
          </>
        )}
        <ChevronDown size={16} />
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          marginTop: 4,
          background: currentThemeName === 'dark' 
            ? 'rgba(30, 41, 59, 0.95)' 
            : 'rgba(255, 255, 255, 0.95)',
          border: currentThemeName === 'dark'
            ? '1px solid rgba(59, 130, 246, 0.2)'
            : '1px solid rgba(59, 130, 246, 0.1)',
          borderRadius: 12,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          zIndex: 1000,
          backdropFilter: 'blur(10px)',
          overflow: 'hidden'
        }}>
          {BULK_ACTIONS.map((action) => {
            const Icon = action.icon;
            return (
              <button
                key={action.value}
                onClick={() => {
                  setBulkAction(action.value);
                  setIsOpen(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'transparent',
                  color: currentThemeName === 'dark' ? '#e5e7eb' : theme.text,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  fontSize: 14,
                  transition: 'background-color 0.2s ease',
                  borderBottom: currentThemeName === 'dark' 
                    ? '1px solid rgba(59, 130, 246, 0.1)' 
                    : '1px solid rgba(59, 130, 246, 0.05)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = currentThemeName === 'dark' 
                    ? 'rgba(59, 130, 246, 0.1)' 
                    : 'rgba(59, 130, 246, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                aria-label={action.label}
              >
                <Icon size={16} style={{ color: action.color }} />
                <span style={{ flex: 1, textAlign: 'left' }}>{action.label}</span>
                {bulkAction === action.value && (
                  <Check size={16} style={{ color: '#3b82f6' }} />
                )}
              </button>
            );
          })}
        </div>
      )}

      {bulkAction && (
        <button
          onClick={onBulkAction}
          style={{
            marginLeft: 12,
            padding: '12px 20px',
            borderRadius: 12,
            border: 'none',
            background: currentThemeName === 'dark' 
              ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' 
              : 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
            color: 'white',
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = 'none';
          }}
          aria-label={`Apply ${selectedAction?.label}`}
        >
          <Shield size={16} />
          Apply
        </button>
      )}
    </div>
  );
};

function UserFilters({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  selectedUsers,
  bulkAction,
  setBulkAction,
  onBulkAction,
  showFilters,
  setShowFilters,
  theme,
  currentThemeName
}) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Calculate filter counts (this would come from props in real implementation)
  const filterOptionsWithCounts = useMemo(() => {
    return FILTER_OPTIONS.map(option => ({
      ...option,
      count: Math.floor(Math.random() * 10) // Simulated count
    }));
  }, []);

  return (
    <div className="animate-fade-in-up" style={{ 
      marginBottom: 24,
      position: 'relative',
      zIndex: 1
    }}>
      <div style={{
        background: currentThemeName === 'dark'
          ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(15, 23, 42, 0.95) 100%)'
          : currentThemeName === 'beige'
          ? 'linear-gradient(135deg, rgba(245, 245, 220, 0.95) 0%, rgba(250, 235, 215, 0.98) 100%)'
          : 'linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(248, 250, 252, 0.99) 100%)',
        border: currentThemeName === 'dark'
          ? '1px solid rgba(59, 130, 246, 0.2)'
          : currentThemeName === 'beige'
          ? '1px solid rgba(139, 69, 19, 0.15)'
          : '1px solid rgba(59, 130, 246, 0.1)',
        borderRadius: 20,
        padding: 24,
        boxShadow: currentThemeName === 'dark'
          ? '0 8px 32px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(59, 130, 246, 0.1)'
          : currentThemeName === 'beige'
          ? '0 8px 32px rgba(139, 69, 19, 0.1), 0 0 0 1px rgba(139, 69, 19, 0.05)'
          : '0 8px 32px rgba(59, 130, 246, 0.08), 0 0 0 1px rgba(59, 130, 246, 0.05)',
        backdropFilter: 'blur(10px)'
      }}>
        {/* Main Filter Bar */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap'
        }}>
          {/* Search Input */}
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search users by name or email..."
            theme={theme}
            currentThemeName={currentThemeName}
            onClear={() => setSearchTerm('')}
          />

          {/* Status Filter */}
          <FilterDropdown
            value={statusFilter}
            onChange={setStatusFilter}
            options={filterOptionsWithCounts}
            theme={theme}
            currentThemeName={currentThemeName}
            isOpen={isFilterOpen}
            onToggle={() => setIsFilterOpen(!isFilterOpen)}
          />

          {/* Bulk Actions */}
          <BulkActions
            selectedUsers={selectedUsers}
            bulkAction={bulkAction}
            setBulkAction={setBulkAction}
            onBulkAction={onBulkAction}
            theme={theme}
            currentThemeName={currentThemeName}
          />

          {/* Additional Actions */}
          <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
            <button
              onClick={() => setShowFilters(!showFilters)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 16px',
                borderRadius: 12,
                border: currentThemeName === 'dark' 
                  ? '1px solid rgba(59, 130, 246, 0.2)' 
                  : '1px solid rgba(59, 130, 246, 0.1)',
                background: currentThemeName === 'dark' 
                  ? 'rgba(30, 41, 59, 0.8)' 
                  : 'rgba(255, 255, 255, 0.9)',
                color: currentThemeName === 'dark' ? 'white' : theme.text,
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(10px)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = currentThemeName === 'dark' 
                  ? 'rgba(59, 130, 246, 0.3)' 
                  : 'rgba(59, 130, 246, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = currentThemeName === 'dark' 
                  ? 'rgba(59, 130, 246, 0.2)' 
                  : 'rgba(59, 130, 246, 0.1)';
              }}
              aria-label="Toggle advanced filters"
            >
              <Filter size={16} />
              <span>Filters</span>
            </button>

            <button
              onClick={() => {/* Handle export */}}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 16px',
                borderRadius: 12,
                border: currentThemeName === 'dark' 
                  ? '1px solid rgba(59, 130, 246, 0.2)' 
                  : '1px solid rgba(59, 130, 246, 0.1)',
                background: currentThemeName === 'dark' 
                  ? 'rgba(30, 41, 59, 0.8)' 
                  : 'rgba(255, 255, 255, 0.9)',
                color: currentThemeName === 'dark' ? 'white' : theme.text,
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(10px)'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = currentThemeName === 'dark' 
                  ? 'rgba(59, 130, 246, 0.3)' 
                  : 'rgba(59, 130, 246, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = currentThemeName === 'dark' 
                  ? 'rgba(59, 130, 246, 0.2)' 
                  : 'rgba(59, 130, 246, 0.1)';
              }}
              aria-label="Export users"
            >
              <Download size={16} />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* Advanced Filters (Collapsible) */}
        {showFilters && (
          <div style={{
            marginTop: 20,
            paddingTop: 20,
            borderTop: currentThemeName === 'dark' 
              ? '1px solid rgba(59, 130, 246, 0.1)' 
              : '1px solid rgba(59, 130, 246, 0.05)'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 16
            }}>
              {/* Date Range Filter */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: currentThemeName === 'dark' ? '#9ca3af' : theme.textTertiary,
                  marginBottom: 8,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}>
                  Signup Date Range
                </label>
                <select
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: currentThemeName === 'dark' 
                      ? '1px solid rgba(59, 130, 246, 0.2)' 
                      : '1px solid rgba(59, 130, 246, 0.1)',
                    background: currentThemeName === 'dark' 
                      ? 'rgba(30, 41, 59, 0.8)' 
                      : 'rgba(255, 255, 255, 0.9)',
                    color: currentThemeName === 'dark' ? 'white' : theme.text,
                    fontSize: 14
                  }}
                >
                  <option value="">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="quarter">This Quarter</option>
                  <option value="year">This Year</option>
                </select>
              </div>

              {/* Business Type Filter */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: currentThemeName === 'dark' ? '#9ca3af' : theme.textTertiary,
                  marginBottom: 8,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}>
                  Business Type
                </label>
                <select
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: currentThemeName === 'dark' 
                      ? '1px solid rgba(59, 130, 246, 0.2)' 
                      : '1px solid rgba(59, 130, 246, 0.1)',
                    background: currentThemeName === 'dark' 
                      ? 'rgba(30, 41, 59, 0.8)' 
                      : 'rgba(255, 255, 255, 0.9)',
                    color: currentThemeName === 'dark' ? 'white' : theme.text,
                    fontSize: 14
                  }}
                >
                  <option value="">All Types</option>
                  <option value="technology">Technology</option>
                  <option value="restaurant">Restaurant</option>
                  <option value="transportation">Transportation</option>
                  <option value="retail">Retail</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="education">Education</option>
                </select>
              </div>

              {/* Role Filter */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: currentThemeName === 'dark' ? '#9ca3af' : theme.textTertiary,
                  marginBottom: 8,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}>
                  User Role
                </label>
                <select
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: currentThemeName === 'dark' 
                      ? '1px solid rgba(59, 130, 246, 0.2)' 
                      : '1px solid rgba(59, 130, 246, 0.1)',
                    background: currentThemeName === 'dark' 
                      ? 'rgba(30, 41, 59, 0.8)' 
                      : 'rgba(255, 255, 255, 0.9)',
                    color: currentThemeName === 'dark' ? 'white' : theme.text,
                    fontSize: 14
                  }}
                >
                  <option value="">All Roles</option>
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                  <option value="moderator">Moderator</option>
                </select>
              </div>

              {/* Last Login Filter */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: currentThemeName === 'dark' ? '#9ca3af' : theme.textTertiary,
                  marginBottom: 8,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}>
                  Last Login
                </label>
                <select
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    borderRadius: 8,
                    border: currentThemeName === 'dark' 
                      ? '1px solid rgba(59, 130, 246, 0.2)' 
                      : '1px solid rgba(59, 130, 246, 0.1)',
                    background: currentThemeName === 'dark' 
                      ? 'rgba(30, 41, 59, 0.8)' 
                      : 'rgba(255, 255, 255, 0.9)',
                    color: currentThemeName === 'dark' ? 'white' : theme.text,
                    fontSize: 14
                  }}
                >
                  <option value="">Any Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="never">Never</option>
                </select>
              </div>
            </div>

            {/* Filter Actions */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 12,
              marginTop: 20
            }}>
              <button
                onClick={() => {/* Clear all filters */}}
                style={{
                  padding: '10px 16px',
                  borderRadius: 8,
                  border: currentThemeName === 'dark' 
                    ? '1px solid rgba(107, 114, 128, 0.3)' 
                    : '1px solid rgba(107, 114, 128, 0.2)',
                  background: 'transparent',
                  color: currentThemeName === 'dark' ? '#9ca3af' : '#6b7280',
                  fontSize: 14,
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = currentThemeName === 'dark' 
                    ? 'rgba(107, 114, 128, 0.1)' 
                    : 'rgba(107, 114, 128, 0.05)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                Clear All
              </button>
              <button
                onClick={() => {/* Apply filters */}}
                style={{
                  padding: '10px 20px',
                  borderRadius: 8,
                  border: 'none',
                  background: currentThemeName === 'dark' 
                    ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' 
                    : 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                  color: 'white',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-1px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default UserFilters; 